import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getFilteredRowModel,
  createColumnHelper,
  Row,
} from '@tanstack/react-table';

// Mocks

// Types
import {
  TableProps,
  activeUsersColumnNames,
  inactiveUsersColumnNames,
  dataTable,
  SearchbarFilterTypes,
} from './types';

// Styled components
import {
  ContainerTable,
  FiltersContainer,
  InactiveUsersTableContainer,
  ActiveUsersTableContainer,
  SwapUserButton,
  SwapUserButtonLeft,
  InputRadioStyled,
  TrStyled,
} from './styles';

import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../summaryTables/SummaryTablesStyles';
import { Typography } from '../../../../Typography';

import { activeUsersMocksData, inactiveUsersMocksData } from './mocks';
import SearchBar from '../../../../../inputs/SearchBar';
import { matchesSearch } from '../../../../../../utils/textSearch';

export const UsersManagementTable = ({
  data,
  onAddInactiveUser,
  onAddActiveUser,
}: TableProps): JSX.Element => {
  const { t } = useTranslation('forms');
  const activeUsersDataAll = data?.activeUsers ?? activeUsersMocksData;
  const inactiveUsersDataAll = data?.inactiveUsers ?? inactiveUsersMocksData;

  const [activeUsersData, setActiveUsersData] = useState(activeUsersDataAll);
  const [inactiveUsersData, setInactiveUsersData] =
    useState(inactiveUsersDataAll);

  const [activeUsersChecked, setActiveUsersChecked] = useState({});
  const [inactiveUsersChecked, setInactiveUsersChecked] = useState({});

  const [filteredActiveUsers, setFilteredActiveUsers] = useState(false);
  const [filteredinactiveUsers, setFilteredinactiveUsers] = useState(false);

  const activeUsersColumnHelper = createColumnHelper<dataTable>();
  const inactiveUsersColumnHelper = createColumnHelper<dataTable>();

  useEffect(() => {
    setActiveUsersData(activeUsersDataAll);
    setInactiveUsersData(inactiveUsersDataAll);
  }, [data]);

  const activeUsersColumns = useMemo(
    () => [
      {
        enableHiding: true,
        id: 'select',
        cell: ({ row }: any) => (
          <InputRadioStyled
            name="activeRadioInput"
            {...{
              checked: row.getIsSelected(), // Returns whether or not the row is selected.
              disabled: !row.getCanSelect(), // Returns whether or not the row can be selected.
              indeterminate: row.getIsSomeSelected(), //Returns whether or not some of the row's sub rows are selected.
              onChange: row.getToggleSelectedHandler(), //Returns a handler that can be used to toggle the row.
            }}
          />
        ),
      },
      activeUsersColumnHelper.accessor((row) => row.name, {
        id: 'firstName',
        header: () => t('management.tableHeaders.name'),
        cell: (info) => info.getValue(),
      }),
      activeUsersColumnHelper.accessor((row) => row.surName, {
        id: 'surName',
        header: () => t('management.tableHeaders.surname'),
      }),
      activeUsersColumnHelper.accessor((row) => row.email, {
        id: 'email',
        header: () => t('management.tableHeaders.email'),
      }),
      activeUsersColumnHelper.accessor((row) => row.rolActive, {
        id: 'rolActive',
        header: () => t('management.tableHeaders.role'),
      }),
      activeUsersColumnHelper.accessor((row) => row.concession, {
        id: 'concession',
        header: () => t('management.tableHeaders.concession'),
      }),
      activeUsersColumnHelper.accessor((row) => row.teams, {
        id: 'teams',
        header: () => t('management.tableHeaders.team', 'Equip'),
        cell: (info) => {
          const teams = info.getValue();
          if (!teams || teams.length === 0) return '-';
          return teams.map((team) => team.name).join(', ');
        },
      }),
    ],
    [
      activeUsersData,
      inactiveUsersData,
      activeUsersChecked,
      inactiveUsersChecked,
      data,
      t,
    ]
  );

  const inactiveUsersColumns = useMemo(
    () => [
      {
        enableHiding: true,
        id: 'select',
        cell: ({ row }: any) => (
          <InputRadioStyled
            name="inactiveRadioInput"
            {...{
              checked: row.getIsSelected(), // Returns whether or not the row is selected.
              disabled: !row.getCanSelect(), // Returns whether or not the row can be selected.
              indeterminate: row.getIsSomeSelected(), //Returns whether or not some of the row's sub rows are selected.
              onChange: row.getToggleSelectedHandler(), //Returns a handler that can be used to toggle the row.
            }}
          />
        ),
      },
      inactiveUsersColumnHelper.accessor((row) => row.name, {
        id: 'firstName',
        header: () => t('management.tableHeaders.name'),
        cell: (info) => info.getValue(),
      }),
      inactiveUsersColumnHelper.accessor((row) => row.surName, {
        id: 'surName',
        header: () => t('management.tableHeaders.surname'),
      }),
      inactiveUsersColumnHelper.accessor((row) => row.email, {
        id: 'email',
        header: () => t('management.tableHeaders.email'),
      }),
      inactiveUsersColumnHelper.accessor((row) => row.rolActive, {
        id: 'rolActive',
        header: () => t('management.tableHeaders.role'),
      }),
      inactiveUsersColumnHelper.accessor((row) => row.concession, {
        id: 'concession',
        header: () => t('management.tableHeaders.concession'),
      }),
      inactiveUsersColumnHelper.accessor((row) => row.teams, {
        id: 'teams',
        header: () => t('management.tableHeaders.team', 'Equip'),
        cell: (info) => {
          const teams = info.getValue();
          if (!teams || teams.length === 0) return '-';
          return teams.map((team) => team.name).join(', ');
        },
      }),
    ],
    [
      activeUsersData,
      inactiveUsersData,
      activeUsersChecked,
      inactiveUsersChecked,
      data,
      t,
    ]
  );

  const activeUsersSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      if (value === '') {
        setActiveUsersData(activeUsersDataAll);
        setFilteredActiveUsers((prev) => !prev);
      } else {
        const filtered = activeUsersDataAll.filter(
          (user) =>
            matchesSearch([`${user.name} ${user.surName ?? ''}`, user.email], value)
        );
        setActiveUsersData(filtered);
      }
    },
    [activeUsersDataAll, filteredActiveUsers]
  );

  const inactiveUsersSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      if (value === '') {
        setInactiveUsersData(inactiveUsersDataAll);
        setFilteredinactiveUsers((prev) => !prev);
      } else {
        const filtered = inactiveUsersDataAll.filter(
          (user) =>
            matchesSearch([`${user.name} ${user.surName ?? ''}`, user.email], value)
        );
        setInactiveUsersData(filtered);
      }
    },
    [inactiveUsersDataAll, filteredinactiveUsers]
  );

  const handleRowClick = (row: Row<dataTable>) => {
    row.getToggleSelectedHandler()(row);
  };

  const addActiveUser = () => {
    const selectedRow = activeUsersTable.getSelectedRowModel().rows[0].original;

    onAddActiveUser(selectedRow.id, selectedRow.email); // Pass both id and email

    activeUsersTable.resetRowSelection(true);
  };

  const addInactiveUser = () => {
    const selectedRow =
      inactiveUsersTable.getSelectedRowModel().rows[0].original;

    onAddInactiveUser(selectedRow.id, selectedRow.email); // Pass both id and email

    inactiveUsersTable.resetRowSelection(true);
  };

  const activeUsersTable = useReactTable({
    getRowId: (row: any) => row.id,
    data: activeUsersData,
    columns: activeUsersColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setActiveUsersChecked,
    state: {
      rowSelection: activeUsersChecked,
    },
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  const inactiveUsersTable = useReactTable({
    getRowId: (row: any) => row.id,
    data: inactiveUsersData,
    columns: inactiveUsersColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setInactiveUsersChecked,
    state: {
      rowSelection: inactiveUsersChecked,
    },
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  return (
    <ContainerTable>
      <ActiveUsersTableContainer>
        <Typography size="L" variant="semiBold">
          {t('management.activeUsers')}
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => activeUsersSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        <TableContainer>
          <Table>
            <Thead>
              {activeUsersTable.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Th>
                  ))}
                </tr>
              ))}
            </Thead>
            <Tbody>
              {activeUsersTable.getRowModel().rows.map((row) => (
                <TrStyled
                  key={row.id}
                  checked={row.getIsSelected()}
                  indeterminate={!activeUsersTable.getIsSomeRowsSelected()}
                  onClick={() => handleRowClick(row)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </TrStyled>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        <SwapUserButtonLeft onClick={addActiveUser}>⮕</SwapUserButtonLeft>
      </ActiveUsersTableContainer>
      <InactiveUsersTableContainer>
        <Typography size="L" variant="semiBold">
          {t('management.inactiveUsers')}
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => inactiveUsersSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        <TableContainer>
          <Table>
            <Thead>
              {inactiveUsersTable.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Th>
                  ))}
                </tr>
              ))}
            </Thead>
            <Tbody>
              {inactiveUsersTable.getRowModel().rows.map((row) => (
                <TrStyled
                  key={row.id}
                  checked={row.getIsSelected()}
                  indeterminate={!inactiveUsersTable.getIsSomeRowsSelected()}
                  onClick={() => handleRowClick(row)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </TrStyled>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
        <SwapUserButton onClick={addInactiveUser}>⬅</SwapUserButton>
      </InactiveUsersTableContainer>
    </ContainerTable>
  );
};
